import { createFileRoute } from '@tanstack/react-router'
import { useState, useMemo, useEffect, useRef } from 'react'
import type { CSSProperties } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Page } from '@/components/layout/page'
import { StaleDataHint } from '@/components/layout/offline-banner'
import { api } from '@/api/http'
import { callNative } from '@/bridge'

export const Route = createFileRoute('/mrgolisoda/more/my-franchise/$franchiseId/clients/')({
  validateSearch: (s: Record<string, unknown>) => ({
    date: typeof s.date === 'string' ? s.date : undefined,
    name: typeof s.name === 'string' ? s.name : undefined,
    business_tag: typeof s.business_tag === 'string' ? s.business_tag : undefined,
  }),
  component: ManagerClientsPage,
})

// ─── Types ────────────────────────────────────────────────────────────────────

interface ClientStatusAPI {
  id: number | null
  name: string
  color: string
}

interface BillStatus {
  count: number
  label: string
}

interface GPData {
  target: string
  achieved: string
  gp_status?: number
}

interface ClientAPI {
  client_id: number
  display_name: string
  client_address: string
  due_amount: string
  visited: boolean
  order_placed: boolean
  client_status: ClientStatusAPI | null
  business_tag: string
  gp: GPData
  bill_status: BillStatus | null
  latitude?: number | null
  longitude?: number | null
}

interface RouteData {
  route_id: number
  route_name: string
  clients: ClientAPI[]
}

interface ManagerClientsResponse {
  date: string
  franchise_id: number
  routes: RouteData[]
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function todayStr(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

type IconShape = 'star' | 'circle' | 'crossed_circle'

function shapeFromTag(tag: string): IconShape {
  if (tag === 'Visicooler') return 'star'
  if (tag === 'Non-Buying') return 'crossed_circle'
  return 'circle'
}

function colorFromStatus(status: ClientStatusAPI | null): string {
  return status?.color ?? '#BDBDBD'
}

function gpStatusColor(s?: number): string {
  if (s === 1) return '#226600'
  if (s === 2) return '#1565C0'
  if (s === 3) return '#E65100'
  return '#9E9E9E'
}

function gpStatusLabel(s?: number): string {
  if (s === 1) return 'At target'
  if (s === 2) return 'Excess'
  if (s === 3) return 'Less achieved'
  return 'Not achieved'
}

// ─── Shape icon ───────────────────────────────────────────────────────────────

function ShapeIcon({ shape, color, size = 30 }: { shape: IconShape; color: string; size?: number }) {
  if (shape === 'star') {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
        <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
      </svg>
    )
  }
  if (shape === 'crossed_circle') {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="9.5" stroke={color} strokeWidth="2" />
        <line x1="6.5" y1="6.5" x2="17.5" y2="17.5" stroke={color} strokeWidth="2.5" strokeLinecap="round" />
        <line x1="17.5" y1="6.5" x2="6.5" y2="17.5" stroke={color} strokeWidth="2.5" strokeLinecap="round" />
      </svg>
    )
  }
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
      <circle cx="12" cy="12" r="10" />
    </svg>
  )
}

// ─── Shimmer ──────────────────────────────────────────────────────────────────

const shimmerStyle: CSSProperties = {
  background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
  backgroundSize: '200% 100%',
  animation: 'shimmer 1.5s infinite',
  borderRadius: 4,
}

function ShimmerList() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} style={{
          background: '#fff', borderRadius: 12, border: '1px solid #E5E5E5',
          boxShadow: '0 1px 4px rgba(0,0,0,0.06)', padding: '12px 14px',
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <div style={{ ...shimmerStyle, width: 30, height: 30, borderRadius: '50%', flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <div style={{ ...shimmerStyle, height: 13, width: '55%', marginBottom: 6 }} />
            <div style={{ ...shimmerStyle, height: 10, width: '35%' }} />
          </div>
          <div style={{ ...shimmerStyle, width: 64, height: 22, borderRadius: 6 }} />
        </div>
      ))}
    </div>
  )
}

// ─── Multi-select filter (same as my-beat) ────────────────────────────────────

interface SelectOption {
  value: string
  label: string
  description?: string
}

function MultiSelectFilter({
  label,
  options,
  selected,
  onChange,
  align = 'left',
}: {
  label: string
  options: SelectOption[]
  selected: string[]
  onChange: (values: string[]) => void
  align?: 'left' | 'right'
}) {
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState<string[]>([])
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    function handleOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleOutside)
    return () => document.removeEventListener('mousedown', handleOutside)
  }, [open])

  function handleOpen() {
    setDraft([...selected])
    setOpen((v) => !v)
  }

  function handleDone() {
    onChange(draft)
    setOpen(false)
  }

  function toggle(v: string) {
    setDraft((d) => d.includes(v) ? d.filter((x) => x !== v) : [...d, v])
  }

  const isActive = selected.length > 0
  const btnLabel = isActive ? `${label} (${selected.length})` : label

  return (
    <div ref={containerRef} style={{ flex: 1, minWidth: 0, position: 'relative' }}>
      <button
        onClick={handleOpen}
        style={{
          width: '100%', padding: '7px 4px', fontSize: 10, fontWeight: 600,
          border: `1.5px solid ${isActive ? '#1A1A1A' : '#DADADA'}`,
          borderRadius: 10, background: isActive ? '#1A1A1A' : '#fff',
          color: isActive ? '#fff' : '#555', cursor: 'pointer',
          fontFamily: 'inherit', textAlign: 'center',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}
      >
        {btnLabel} ▾
      </button>

      {open && (
        <div style={{
          position: 'absolute',
          top: 'calc(100% + 6px)',
          ...(align === 'right' ? { right: 0 } : { left: 0 }),
          minWidth: 224,
          zIndex: 300,
          background: '#fff',
          borderRadius: 12,
          boxShadow: '0 4px 24px rgba(0,0,0,0.18)',
          border: '1px solid #E5E5E5',
          overflow: 'hidden',
        }}>
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '12px 14px 10px', borderBottom: '1px solid #F0F0F0',
          }}>
            <span style={{ fontSize: 12, color: '#808080' }}>All</span>
            <div style={{ display: 'flex', gap: 14 }}>
              <button
                onClick={() => setDraft(options.map((o) => o.value))}
                style={{ fontSize: 12, color: '#2563EB', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}
              >
                Select all
              </button>
              <button
                onClick={() => setDraft([])}
                style={{ fontSize: 12, color: '#2563EB', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}
              >
                Clear
              </button>
            </div>
          </div>

          <div style={{ maxHeight: '40vh', overflowY: 'auto' }}>
            {options.map((o) => (
              <div
                key={o.value}
                onClick={() => toggle(o.value)}
                style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 14px', cursor: 'pointer' }}
              >
                <div style={{
                  width: 18, height: 18, borderRadius: 4, flexShrink: 0,
                  border: draft.includes(o.value) ? 'none' : '2px solid #DADADA',
                  background: draft.includes(o.value) ? '#2563EB' : '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {draft.includes(o.value) && (
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.5">
                      <polyline points="20,6 9,17 4,12" />
                    </svg>
                  )}
                </div>
                <div>
                  <div style={{ fontSize: 13, color: '#1A1A1A', fontWeight: 500 }}>{o.label}</div>
                  {o.description && (
                    <div style={{ fontSize: 11, color: '#9E9E9E', marginTop: 1 }}>· {o.description}</div>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div style={{ padding: '10px 14px 14px', borderTop: '1px solid #F0F0F0' }}>
            <button
              onClick={handleDone}
              style={{
                width: '100%', padding: '12px', borderRadius: 10,
                background: '#2563EB', color: '#fff', fontSize: 14, fontWeight: 700,
                border: 'none', cursor: 'pointer', fontFamily: 'inherit',
              }}
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Static filter options ────────────────────────────────────────────────────

const OUTLET_TYPE_OPTIONS: SelectOption[] = [
  { value: 'Visicooler', label: 'Visicooler' },
  { value: 'Buying', label: 'Buying' },
  { value: 'Non-Buying', label: 'Non-Buying' },
]

const VISIT_TYPE_OPTIONS: SelectOption[] = [
  { value: 'not_visited', label: 'Not visited' },
  { value: 'visited_no_order', label: 'Visited · No order' },
  { value: 'visited_order_received', label: 'Visited · Order received' },
]

const TARGET_OPTIONS: SelectOption[] = [
  { value: '1', label: 'Target achieved', description: '100% and above' },
  { value: '2', label: 'Excess achieved', description: 'above 100%' },
  { value: '3', label: 'Less achieved', description: '1–99%' },
  { value: '4', label: 'Target not achieved', description: '₹0 GP' },
]

// ─── Customer card (not clickable) ────────────────────────────────────────────

function CustomerCard({ client }: { client: ClientAPI }) {
  const shape = shapeFromTag(client.business_tag)
  const color = colorFromStatus(client.client_status)
  const gpTarget = parseFloat(client.gp.target)
  const gpAchieved = parseFloat(client.gp.achieved)

  return (
    <div style={{
      background: '#fff', borderRadius: 12, border: '1px solid #E5E5E5',
      boxShadow: '0 1px 4px rgba(0,0,0,0.06)', padding: '12px 14px',
    }}>
      {/* Row 1 */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <div style={{ flexShrink: 0, marginTop: 2 }}>
          <ShapeIcon shape={shape} color={color} size={30} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#1A1A1A' }}>
              {client.display_name}
            </span>
            <span style={{
              fontSize: 12, fontWeight: 700,
              color: parseFloat(client.due_amount) > 0 ? '#E51414' : '#808080',
              whiteSpace: 'nowrap', flexShrink: 0,
            }}>
              DUE : ₹{parseFloat(client.due_amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 3, gap: 8 }}>
            <span style={{ fontSize: 12, color: '#808080' }}>
              {client.business_tag}
              {client.client_status && (
                <>{' · '}<span style={{ color: client.client_status.color, fontWeight: 600 }}>{client.client_status.name}</span></>
              )}
            </span>
            <span style={{
              fontSize: 10, fontWeight: 700,
              color: client.visited ? '#226600' : '#666666',
              background: client.visited ? '#E8F5E9' : '#F0F0F0',
              border: `1px solid ${client.visited ? '#A5D6A7' : '#E0E0E0'}`,
              borderRadius: 6, padding: '2px 8px', whiteSpace: 'nowrap', flexShrink: 0,
            }}>
              {client.visited ? 'VISITED' : 'NOT VISITED'}
            </span>
          </div>
        </div>
      </div>

      {/* Bill status */}
      {client.bill_status && (
        <div style={{ marginTop: 8 }}>
          <span style={{
            fontSize: 11, color: '#E65100', background: '#FFF3E0',
            border: '1px solid #FFCC80', borderRadius: 20, padding: '2px 10px',
          }}>
            {client.bill_status.label}
          </span>
        </div>
      )}

      {/* GP box */}
      {gpTarget > 0 && (
        <div style={{
          marginTop: 10, padding: '8px 12px',
          border: '1.5px dashed #81C784', borderRadius: 8, background: '#F1F8E9',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8,
        }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: '#1A1A1A' }}>
            GP ₹{gpAchieved.toLocaleString('en-IN')} / ₹{gpTarget.toLocaleString('en-IN')}
          </span>
          <span style={{ fontSize: 11, color: gpStatusColor(client.gp.gp_status), fontWeight: 600, whiteSpace: 'nowrap' }}>
            {gpStatusLabel(client.gp.gp_status)}
          </span>
        </div>
      )}

      {/* Bottom */}
      <div style={{ display: 'flex', alignItems: 'center', marginTop: 10, gap: 8 }}>
        <span style={{
          fontSize: 11, color: '#9E9E9E', flex: 1, minWidth: 0,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {client.client_address || '—'}
        </span>
        {client.latitude != null && client.longitude != null && (
          <button
            onClick={() => callNative('openExternal', {
              url: `https://www.google.com/maps/search/?api=1&query=${client.latitude},${client.longitude}`,
            })}
            style={{
              width: 32, height: 32, borderRadius: '50%', border: '2px solid #22C55E',
              background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', padding: 0, flexShrink: 0,
            }}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#22C55E" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
              <circle cx="12" cy="9" r="2.5" fill="#22C55E" stroke="none"/>
            </svg>
          </button>
        )}
      </div>
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

function ManagerClientsPage() {
  const { franchiseId } = Route.useParams()
  const { date: dateParam, name: franchiseName = 'Clients', business_tag: initialTag } = Route.useSearch()
  const date = dateParam ?? todayStr()

  const [selectedRouteIds, setSelectedRouteIds] = useState<string[]>([])
  const [selectedTags, setSelectedTags] = useState<string[]>(() => initialTag ? [initialTag] : [])
  const [selectedGpStatus, setSelectedGpStatus] = useState<string[]>([])
  const [selectedVisitTypes, setSelectedVisitTypes] = useState<string[]>([])
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')

  const qs = useMemo(() => {
    const p = new URLSearchParams({ franchise_id: franchiseId, date })
    return p.toString()
  }, [franchiseId, date])

  const { data, isLoading, isPaused, error, refetch } = useQuery({
    queryKey: ['manager-route-clients', qs],
    queryFn: () => api.get<ManagerClientsResponse>(
      `/field-customization/goli-soda/manager-route-clients/?${qs}`
    ),
  })

  const routes = useMemo(() => data?.routes ?? [], [data])

  const allClients = useMemo(() =>
    routes.flatMap((r) =>
      r.clients.map((c) => ({ client: c, routeName: r.route_name, routeId: r.route_id }))
    ),
    [routes],
  )

  const filtered = useMemo(() => allClients.filter(({ client, routeId }) => {
    if (selectedRouteIds.length > 0 && !selectedRouteIds.includes(String(routeId))) return false

    if (selectedTags.length > 0 && !selectedTags.includes(client.business_tag)) return false

    if (selectedGpStatus.length > 0) {
      const gpS = String(client.gp.gp_status ?? 4)
      if (!selectedGpStatus.includes(gpS)) return false
    }

    if (selectedVisitTypes.length > 0) {
      const match = selectedVisitTypes.some((t) => {
        if (t === 'not_visited') return !client.visited
        if (t === 'visited_no_order') return client.visited && !client.order_placed
        if (t === 'visited_order_received') return client.visited && client.order_placed
        return false
      })
      if (!match) return false
    }

    if (statusFilter && client.client_status?.name !== statusFilter) return false

    if (search) {
      const q = search.toLowerCase()
      if (
        !client.display_name.toLowerCase().includes(q) &&
        !client.client_address.toLowerCase().includes(q)
      ) return false
    }

    return true
  }), [allClients, selectedRouteIds, selectedTags, selectedGpStatus, selectedVisitTypes, statusFilter, search])

  const statusOptions = useMemo(() => {
    const seen = new Map<string, string>()
    for (const { client } of allClients) {
      if (client.client_status && !seen.has(client.client_status.name)) {
        seen.set(client.client_status.name, client.client_status.color)
      }
    }
    return Array.from(seen.entries()).map(([name, color]) => ({ name, color }))
  }, [allClients])

  const countForStatus = (name: string) =>
    allClients.filter((x) => x.client.client_status?.name === name).length

  const routeOptions: SelectOption[] = routes.map((r) => ({
    value: String(r.route_id),
    label: r.route_name,
  }))

  const hasData = !!data
  const pageTitle = initialTag ? `${franchiseName} · ${initialTag}` : franchiseName

  return (
    <Page title={pageTitle} back scaled>
      {isPaused && hasData && <StaleDataHint />}
      <style>{`
        @keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
        .no-scrollbar::-webkit-scrollbar { display: none }
      `}</style>

      <div style={{ padding: '24px 14px 0' }}>
        {/* Title row */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#1A1A1A', letterSpacing: 0.3 }}>
            ALL ROUTES
          </span>
          <span style={{
            fontSize: 13, fontWeight: 700, color: '#1A1A1A',
            background: '#F0F0F0', border: '1.5px solid #DADADA',
            borderRadius: 20, padding: '3px 12px',
          }}>
            {allClients.length}
          </span>
        </div>

        {/* Dropdown filters — dashed green */}
        <div style={{ border: '1.5px dashed #81C784', borderRadius: 12, padding: '10px', marginBottom: 8 }}>
          <div style={{ display: 'flex', gap: 6 }}>
            <MultiSelectFilter
              label="Route"
              options={routeOptions}
              selected={selectedRouteIds}
              onChange={setSelectedRouteIds}
            />
            <MultiSelectFilter
              label="Outlet type"
              options={OUTLET_TYPE_OPTIONS}
              selected={selectedTags}
              onChange={setSelectedTags}
            />
            <MultiSelectFilter
              label="Visit type"
              options={VISIT_TYPE_OPTIONS}
              selected={selectedVisitTypes}
              onChange={setSelectedVisitTypes}
              align="right"
            />
            <MultiSelectFilter
              label="Target vs Ach."
              options={TARGET_OPTIONS}
              selected={selectedGpStatus}
              onChange={setSelectedGpStatus}
              align="right"
            />
          </div>
        </div>

        {/* Search — dashed green */}
        <div style={{
          border: '1.5px dashed #81C784', borderRadius: 12, padding: '2px 12px',
          display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, background: '#fff',
        }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#AAAAAA" strokeWidth="2.5" style={{ flexShrink: 0 }}>
            <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="text"
            placeholder="Search outlet name, code or route"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              flex: 1, border: 'none', outline: 'none', fontSize: 13,
              color: '#1A1A1A', background: 'transparent', padding: '10px 0',
              fontFamily: 'inherit',
            }}
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#AAAAAA', padding: 0, lineHeight: 1 }}
            >
              ×
            </button>
          )}
        </div>

        {/* Status pills — dynamic */}
        {statusOptions.length > 0 && (
          <div className="no-scrollbar" style={{ display: 'flex', gap: 8, overflowX: 'auto', scrollbarWidth: 'none', paddingBottom: 10 }}>
            {statusOptions.map((s) => {
              const active = statusFilter === s.name
              return (
                <button
                  key={s.name}
                  onClick={() => setStatusFilter(active ? '' : s.name)}
                  style={{
                    flexShrink: 0, display: 'flex', alignItems: 'center', gap: 6,
                    padding: '6px 14px', borderRadius: 20, cursor: 'pointer',
                    fontFamily: 'inherit', fontSize: 12, fontWeight: 500,
                    background: active ? '#F0F0F0' : '#fff',
                    border: `1.5px solid ${active ? '#1A1A1A' : '#DADADA'}`,
                    color: '#1A1A1A',
                  }}
                >
                  <span style={{
                    width: 10, height: 10, borderRadius: '50%',
                    background: s.color, flexShrink: 0, display: 'inline-block',
                  }} />
                  {s.name} ({countForStatus(s.name)})
                </button>
              )
            })}
          </div>
        )}
      </div>

      {/* Cards */}
      <div style={{ padding: '0 14px 40px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {isLoading ? (
          <ShimmerList />
        ) : !hasData && !isPaused && error ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: 32 }}>
            <p style={{ fontSize: 13, color: '#6b737c', textAlign: 'center' }}>{(error as Error).message}</p>
            <button
              onClick={() => refetch()}
              style={{ padding: '8px 20px', borderRadius: 8, background: '#104BFC', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 700, fontFamily: 'inherit' }}
            >
              Retry
            </button>
          </div>
        ) : filtered.length === 0 ? (
          <p style={{ textAlign: 'center', padding: '32px 0', fontSize: 13, color: '#808080' }}>
            No customers
          </p>
        ) : (
          filtered.map(({ client }) => (
            <CustomerCard key={client.client_id} client={client} />
          ))
        )}
      </div>
    </Page>
  )
}
